import type { CustomType } from "@prismicio/types-internal/lib/customtypes"
import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useContext,
  useMemo,
} from "react"

type CustomTypeFormat = CustomType["format"]

interface FieldContextValue {
  customTypeFormat: CustomTypeFormat
  isInSlice: boolean
  isInGroup: boolean
  isInRepeatable: boolean
}

const context = createContext<FieldContextValue | undefined>(undefined)

interface RootFieldContextProviderProps {
  customTypeFormat: CustomTypeFormat
  children: ReactNode
}

/**
 * Provides extra context to the fields below.
 * Use it only once at the root of the fields tree.
 */
export function RootFieldContextProvider(props: RootFieldContextProviderProps) {
  const { customTypeFormat, children } = props

  const value = useMemo(
    () => ({
      customTypeFormat,
      isInSlice: false,
      isInGroup: false,
      isInRepeatable: false,
    }),
    [customTypeFormat],
  )

  return <context.Provider value={value}>{children}</context.Provider>
}

/**
 * Overrides the field context to indicate that the field is in a slice.
 */
export function FieldInSliceContextProvider(props: PropsWithChildren) {
  const { children } = props

  const value = useContext(context)

  const newValue = useMemo(
    () => value && { ...value, isInSlice: true },
    [value],
  )

  return <context.Provider value={newValue}>{children}</context.Provider>
}

/**
 * Overrides the field context to indicate that the field is in a group.
 */
export function FieldInGroupContextProvider(props: PropsWithChildren) {
  const { children } = props

  const value = useContext(context)

  const newValue = useMemo(
    () => value && { ...value, isInGroup: true },
    [value],
  )

  return <context.Provider value={newValue}>{children}</context.Provider>
}

/**
 * Overrides the field context to indicate that the field is repeatable.
 */
export function FieldInRepeatableContextProvider(props: PropsWithChildren) {
  const { children } = props

  const value = useContext(context)

  const newValue = useMemo(
    () => value && { ...value, isInRepeatable: true },
    [value],
  )

  return <context.Provider value={newValue}>{children}</context.Provider>
}

export function useFieldContext() {
  return useContext(context)
}
